import { StatusTag } from "@/views/components/StatusTag";
import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Input, Select, Space, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { dateFormat, dateTimeFormat } from "@/utils";
import { confirmDialog } from "@/utils/confirmDialog";
import { OIL_ORDER_PAY_STATE, OIL_ORDER_STATE, OIL_ORDER_TYPE, TIME_PERIOD } from "@zhihaoscm/meta";
import { normalizeOperationActions } from "@/views/components/ProTable";
import { OilOrderPlanDialog } from "./OilOrderPlanDialog";
import { OilOrderConfirmDialog, type OilOrderConfirmForm } from "./OilOrderConfirmDialog";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

export interface OilOrderRow {
    oilOrder: {
        id: string | number;
        shipId?: string | number;
        shipCnName?: string;
        shipName?: string;
        captainId?: string | number;
        captainName?: string;
        captainMobile?: string;
        captainInfo?: { name?: string; realName?: string; mobile?: string; code?: string };
        state?: number;
        beforeCancelState?: number;
        orderType?: number;
        payState?: string | number;
        isAgreeProtocol?: boolean;
        bookingRefuelingVolume?: number;
        bookingRefuelingPrice?: number;
        bookingRefuelingTime?: string;
        bookingRefuelingTimePeriod?: string | number;
        oilType?: string | number;
        actualRefuelingTime?: string;
        createdTime?: string;
        planInfo?: {
            refuelingVolume?: number;
            refuelingTime?: string;
            refuelingTimePeriod?: string | number;
            refuelingPrice?: number;
            oilType?: number;
            planByName?: string;
        };
    };
    oilSite?: { id?: string | number; name?: string; address?: string };
}

export interface OilOrderPlanForm {
    oilSiteId: string | number;
    planInfo: {
        oilType?: number;
        refuelingVolume?: number;
        refuelingTime?: string;
        refuelingTimePeriod?: string | number;
        refuelingPrice?: number;
    };
}

export type { OilOrderConfirmForm } from "./OilOrderConfirmDialog";

export interface OilOrderPageParams {
    page: number;
    size: number;
    shipName?: string;
    customerInfo?: string;
    siteName?: string;
    state?: number;
    orderType?: number;
    payState?: number;
    beginTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface OilOrderPageProps {
    loadOrders?: (params: OilOrderPageParams) => Promise<{ list: OilOrderRow[]; total: number }>;
    checkOrder?: (id: string | number) => Promise<unknown>;
    reportPlan?: (row: OilOrderRow) => Promise<unknown>;
    planOrder?: (id: string | number, form: OilOrderPlanForm) => Promise<unknown>;
    confirmOil?: (row: OilOrderRow) => Promise<unknown>;
    confirmOrder?: (id: string | number, form: OilOrderConfirmForm) => Promise<unknown>;
    completeOil?: (id: string | number) => Promise<unknown>;
    cancelOrder?: (id: string | number) => Promise<unknown>;
    deleteOrder?: (id: string | number) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 油品订单快照分页接口。 */
export async function loadOilOrders(params: OilOrderPageParams) {
    const response = await shippingRequestHttp.service.get<{ list?: OilOrderRow[]; total?: number }>(
        "/oil-order-snapshot/paging",
        { params }
    );
    return { list: response.data.list || [], total: response.data.total || 0 };
}

/** 复用 Vue 油品订单核对接口。 */
export async function checkOilOrder(id: string | number) {
    const response = await shippingRequestHttp.service.put(`/oil-order-snapshot/check-refueling/${id}`, {});
    return response.data;
}

/** 复用 Vue 油品订单取消接口。 */
export async function cancelOilOrder(id: string | number) {
    const response = await shippingRequestHttp.service.put(`/oil-order-snapshot/cancel/${id}`);
    return response.data;
}

/** 复用 Vue 油品订单完成接口。 */
export async function completeOilOrder(id: string | number) {
    const response = await shippingRequestHttp.service.put(`/oil-order-snapshot/complete/${id}`);
    return response.data;
}

/** 复用 Vue 油品订单删除接口。 */
export async function deleteOilOrder(id: string | number) {
    const response = await shippingRequestHttp.service.delete(`/oil-order-snapshot/delete/${id}`);
    return response.data;
}

/** 复用 Vue 油品订单报计划接口。 */
export async function planOilOrder(id: string | number, form: OilOrderPlanForm) {
    const response = await shippingRequestHttp.service.put(`/oil-order-snapshot/plan/${id}`, form);
    return response.data;
}

/** 复用 Vue 油品订单确认加油接口。 */
export async function confirmOilOrder(id: string | number, form: OilOrderConfirmForm) {
    const response = await shippingRequestHttp.service.put(`/oil-order-snapshot/confirm-refueling/${id}`, form);
    return response.data;
}

const stateText = (state?: number) => OIL_ORDER_STATE.getIntroByCode(state) || DEFAULT_VALUE;
const orderTypeText = (value?: number) => OIL_ORDER_TYPE.getIntroByCode(value) || DEFAULT_VALUE;
const payStateText = (value?: number | string) => OIL_ORDER_PAY_STATE.getIntroByCode(value) || DEFAULT_VALUE;

function timeText(row: OilOrderRow) {
    const order = row.oilOrder;
    if (order.actualRefuelingTime) return dateTimeFormat(order.actualRefuelingTime);
    const refuelingTime = order.planInfo?.refuelingTime || order.bookingRefuelingTime;
    const period = order.planInfo?.refuelingTimePeriod || order.bookingRefuelingTimePeriod;
    return refuelingTime
        ? `${dateFormat(refuelingTime)} ${TIME_PERIOD.getIntroByCode(period) || DEFAULT_VALUE}`
        : DEFAULT_VALUE;
}

/** 油品订单管理列表，保留 Vue 字段、筛选和状态操作显示逻辑。 */
export function OilOrderPage({
    loadOrders = loadOilOrders,
    checkOrder = checkOilOrder,
    reportPlan,
    planOrder = planOilOrder,
    confirmOil,
    confirmOrder = confirmOilOrder,
    completeOil = completeOilOrder,
    cancelOrder = cancelOilOrder,
    deleteOrder = deleteOilOrder,
    permissions
}: OilOrderPageProps) {
    const navigate = useNavigate();
    // 保持 Vue v-auth 语义：调用方显式传权限时按权限控制，默认兼容现有路由注入方式。
    const canDeal = permissions === undefined || permissions.includes("ROLE_GAS_ORDER_DEAL");
    const canManage = permissions === undefined || permissions.includes("ROLE_GAS_ORDER_MANAGE");
    const [rows, setRows] = useState<OilOrderRow[]>([]);
    const [total, setTotal] = useState(0);
    const [keyword, setKeyword] = useState("");
    const [customerInfo, setCustomerInfo] = useState("");
    const [siteName, setSiteName] = useState("");
    const [state, setState] = useState<number>();
    const [orderType, setOrderType] = useState<number>();
    const [payState, setPayState] = useState<number>();
    const [dateRange, setDateRange] = useState<[string, string] | null>(null);
    const [sortOrder, setSortOrder] = useState<string>();
    const [errorMessage, setErrorMessage] = useState("");
    const [planRow, setPlanRow] = useState<OilOrderRow>();
    const [confirmRow, setConfirmRow] = useState<OilOrderRow>();

    const load = useCallback(async () => {
        try {
            const result = await loadOrders({
                page: 1,
                size: 10,
                shipName: keyword || undefined,
                customerInfo: customerInfo || undefined,
                siteName: siteName || undefined,
                state,
                orderType,
                payState,
                ...(dateRange?.[0] ? { beginTime: dateFormat(dateRange[0], LOG_DATE_FORMAT_START) } : {}),
                ...(dateRange?.[1] ? { endTime: dateFormat(dateRange[1], LOG_DATE_FORMAT_END) } : {}),
                ...(sortOrder ? { sortKey: "booking_time", sortOrder } : {})
            });
            setRows(result.list);
            setTotal(result.total);
            setErrorMessage("");
        } catch {
            setErrorMessage("油品订单加载失败");
        }
    }, [customerInfo, dateRange, keyword, loadOrders, orderType, payState, siteName, sortOrder, state]);

    // 对齐旧 Vue 下单时间自定义排序参数，避免只改变表头而未改变后端排序结果。
    const handleSortChange = ({ prop, order }: { prop?: string; order?: string | null }) => {
        setSortOrder(prop === "bookingTime" && order ? order : undefined);
    };

    useEffect(() => {
        void load();
    }, [load]);

    const runAction = useCallback(
        async (action: () => Promise<unknown>, successText: string, confirmMessage?: string) => {
            if (confirmMessage && !(await confirmDialog(confirmMessage))) return;
            try {
                await action();
                notifyOperationSuccess(successText);
                await load();
            } catch {
                setErrorMessage("油品订单操作失败");
            }
        },
        [load]
    );

    const operation = useCallback(
        (row: OilOrderRow) => {
            const order = row.oilOrder;
            const items = [];
            if (canDeal && order.state === 1 && (order.orderType !== 2 || order.isAgreeProtocol)) {
                items.push(
                    <Button
                        key="check"
                        aria-label="核对"
                        size="small"
                        onClick={() => void runAction(() => checkOrder(order.id), "已核对")}
                    >
                        核对
                    </Button>
                );
            }
            if (canDeal && order.state === 2 && !order.planInfo) {
                items.push(
                    <Button
                        key="plan"
                        aria-label="报计划"
                        size="small"
                        onClick={() => {
                            if (reportPlan) void runAction(() => reportPlan(row), "已报计划");
                            else setPlanRow(row);
                        }}
                    >
                        报计划
                    </Button>
                );
            }
            if (canDeal && order.state === 2 && order.planInfo && !order.actualRefuelingTime) {
                items.push(
                    <Button
                        key="confirm"
                        aria-label="确认加油"
                        size="small"
                        onClick={() => {
                            if (confirmOil) {
                                void runAction(() => confirmOil(row), "已确认加油");
                                return;
                            }
                            if (Number(order.payState) !== 2) {
                                message.warning("此油品订单油费待确认，请确认财税收到此油款");
                                return;
                            }
                            setConfirmRow(row);
                        }}
                    >
                        确认加油
                    </Button>
                );
            }
            if (canDeal && order.state === 2 && order.actualRefuelingTime) {
                items.push(
                    <Button
                        key="complete"
                        aria-label="完成加油"
                        size="small"
                        onClick={() => void runAction(() => completeOil(order.id), "已完成加油")}
                    >
                        完成加油
                    </Button>
                );
            }
            if (canDeal && (order.state === 1 || order.state === 2)) {
                items.push(
                    <Button
                        key="cancel"
                        aria-label="取消"
                        size="small"
                        onClick={() => void runAction(() => cancelOrder(order.id), "已取消", "是否取消该油品订单？")}
                    >
                        取消
                    </Button>
                );
            }
            if (canManage && order.state === 4) {
                items.push(
                    <Button
                        key="delete"
                        aria-label="删除"
                        size="small"
                        onClick={() => void runAction(() => deleteOrder(order.id), "已删除", "是否删除该油品订单？")}
                    >
                        删除
                    </Button>
                );
            }
            // 旧 Vue Table 在操作项超过三项时折叠为“更多”，避免操作列被撑宽。
            return normalizeOperationActions(<Space>{items}</Space>);
        },
        [canDeal, canManage, cancelOrder, checkOrder, completeOil, confirmOil, deleteOrder, reportPlan, runAction]
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 油品订单 ZhTable 的状态字段与 operation slot。
    const oilOrderColumns = useMemo<TableColumnProps<OilOrderRow>[]>(
        () => [
            {
                key: "id",
                label: "编号",
                width: 148,
                prop: "oilOrder",
                render: ({ row }) => (
                    <Link to={`/logistics/oil/oilTypeManagement/detail/${row.oilOrder.id}`}>{row.oilOrder.id}</Link>
                )
            },
            {
                key: "shipName",
                label: "船舶",
                minWidth: 176,
                prop: "oilOrder",
                render: ({ row }) => {
                    if (!row.oilOrder.shipCnName && !row.oilOrder.shipName) return DEFAULT_VALUE;
                    if (!row.oilOrder.shipCnName) return row.oilOrder.shipName;
                    if (!row.oilOrder.shipName) return row.oilOrder.shipCnName;
                    return (
                        <>
                            <span>{row.oilOrder.shipCnName}</span> / <span>{row.oilOrder.shipName}</span>
                        </>
                    );
                }
            },
            {
                key: "customerInfo",
                label: "承运商账号",
                minWidth: 176,
                prop: "oilOrder",
                render: ({ row }) => {
                    const info = row.oilOrder.captainInfo;
                    return [
                        info?.name || info?.realName || row.oilOrder.captainName,
                        info?.mobile || row.oilOrder.captainMobile,
                        info?.code
                    ]
                        .map(value => value || DEFAULT_VALUE)
                        .join(" | ");
                }
            },
            {
                key: "siteName",
                label: "站点",
                minWidth: 176,
                prop: "oilSite",
                render: ({ row }) => row.oilSite?.name || "-"
            },
            {
                key: "refuelingVolume",
                label: "加油量(吨)",
                width: 92,
                prop: "oilOrder",
                render: ({ row }) =>
                    row.oilOrder.planInfo?.refuelingVolume || row.oilOrder.bookingRefuelingVolume || "-"
            },
            {
                key: "refuelingTime",
                label: "加油时间",
                width: 190,
                prop: "oilOrder",
                render: ({ row }) => timeText(row)
            },
            {
                key: "state",
                label: "业务状态",
                width: 106,
                prop: "oilOrder",
                render: ({ row }) => <StatusTag>{stateText(row.oilOrder.state)}</StatusTag>
            },
            {
                key: "bookingTime",
                label: "下单时间",
                width: 158,
                prop: "oilOrder",
                sortable: true,
                render: ({ row }) =>
                    row.oilOrder.createdTime ? dateTimeFormat(row.oilOrder.createdTime) : DEFAULT_VALUE
            },
            {
                key: "orderType",
                label: "订单方式",
                width: 120,
                prop: "oilOrder",
                render: ({ row }) => orderTypeText(row.oilOrder.orderType)
            },
            {
                key: "payState",
                label: "支付状态",
                width: 92,
                prop: "oilOrder",
                render: ({ row }) => <StatusTag>{payStateText(row.oilOrder.payState)}</StatusTag>
            },
            {
                key: "operation",
                label: "操作",
                width: 240,
                prop: "operation",
                fixed: "right",
                render: ({ row }) => operation(row)
            }
        ],
        [operation]
    );

    return (
        <section aria-label="oil-order-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Space wrap>
                    <Input
                        aria-label="船舶名称"
                        placeholder="请输入船舶名称或编号搜索"
                        value={keyword}
                        onChange={event => setKeyword(event.target.value)}
                    />
                    <Input
                        aria-label="承运商账号"
                        placeholder="请输入承运商账号信息搜索"
                        value={customerInfo}
                        onChange={event => setCustomerInfo(event.target.value)}
                    />
                    <Input
                        aria-label="站点名称"
                        placeholder="请输入站点名称搜索"
                        value={siteName}
                        onChange={event => setSiteName(event.target.value)}
                    />
                    <ZhDateRangePicker
                        format="YYYY-MM-DD"
                        rangeSeparator="-"
                        startPlaceholder="加油开始时间"
                        endPlaceholder="加油结束时间"
                        type="daterange"
                        value={dateRange}
                        onChange={(_value, formattedValue) => setDateRange(formattedValue)}
                    />
                    <Select
                        aria-label="订单方式"
                        allowClear
                        placeholder="请选择订单方式"
                        value={orderType}
                        options={OIL_ORDER_TYPE.options}
                        onChange={value => setOrderType(value)}
                    />
                    <Select
                        aria-label="业务状态"
                        allowClear
                        placeholder="请选择业务状态"
                        value={state}
                        options={[1, 2, 3, 4].map(value => ({ value, label: stateText(value) }))}
                        onChange={value => setState(value)}
                    />
                    <Select
                        aria-label="支付状态"
                        allowClear
                        placeholder="请选择支付状态"
                        value={payState}
                        options={OIL_ORDER_PAY_STATE.options}
                        onChange={value => setPayState(value)}
                    />
                    <Button type="primary" onClick={() => void load()}>
                        搜索
                    </Button>
                    <Button
                        onClick={() => {
                            setKeyword("");
                            setCustomerInfo("");
                            setSiteName("");
                            setState(undefined);
                            setOrderType(undefined);
                            setPayState(undefined);
                            setDateRange(null);
                        }}
                    >
                        重置
                    </Button>
                    {canDeal ? (
                        <Button type="primary" onClick={() => navigate("/logistics/oil/oilTypeManagement/add")}>
                            新增
                        </Button>
                    ) : null}
                </Space>
            </Card>
            <Card>
                <ZhTable<OilOrderRow>
                    rowKey={row => row.oilOrder.id}
                    data={rows}
                    columns={oilOrderColumns}
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination current={1} pageSize={10} total={total} hideOnSinglePage />
                        </div>
                    }
                />
            </Card>
            <OilOrderPlanDialog
                open={Boolean(planRow)}
                row={planRow}
                onCancel={() => setPlanRow(undefined)}
                onSubmit={async form => {
                    if (!planRow) return;
                    await runAction(() => planOrder(planRow.oilOrder.id, form), "已报计划");
                    setPlanRow(undefined);
                }}
            />
            <OilOrderConfirmDialog
                open={Boolean(confirmRow)}
                onCancel={() => setConfirmRow(undefined)}
                onSubmit={async form => {
                    if (!confirmRow) return;
                    await runAction(() => confirmOrder(confirmRow.oilOrder.id, form), "已确认加油");
                    setConfirmRow(undefined);
                }}
            />
        </section>
    );
}

export default OilOrderPage;
